"use client";

import { useState } from "react";
import Image from "next/image";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { SiYoutube } from "react-icons/si";

interface YouTubeVerificationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onStartVerification: () => void;
}

export default function YouTubeVerificationModal({
  isOpen,
  onClose,
  onStartVerification,
}: YouTubeVerificationModalProps) {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  // 구독 인증 안내 이미지들
  const verificationImages = [
    {
      src: "/summary-pdf/youtube-verification/1.webp",
      alt: "구독 인증 안내 1",
      description: "'Google에서 확인하지 않은 앱' 팝업이 나오면 '고급' 클릭"
    },
    {
      src: "/summary-pdf/youtube-verification/2.webp", 
      alt: "구독 인증 안내 2",
      description: "하단의 '정처기 감자로 이동(안전하지 않음)' 클릭"
    },
    {
      src: "/summary-pdf/youtube-verification/3.webp",
      alt: "구독 인증 안내 3", 
      description: "계속을 클릭하고 인증이 완료되면 가격 할인 적용!"
    }
  ];

  const handleNextImage = () => {
    if (currentImageIndex < verificationImages.length - 1) {
      setCurrentImageIndex(currentImageIndex + 1);
    }
  };

  const handlePrevImage = () => {
    if (currentImageIndex > 0) {
      setCurrentImageIndex(currentImageIndex - 1);
    }
  };

  const handleStartVerification = () => {
    onClose();
    setCurrentImageIndex(0);
    onStartVerification();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 backdrop-blur-sm flex items-start justify-center z-50 px-4 pt-16">
      <div className="bg-white rounded-xl max-w-md w-full shadow-2xl">
        {/* 모달 헤더 */}
        <div className="flex items-center justify-between p-3 border-b">
          <h3 className="text-base md:text-lg font-semibold">구독 인증 안내</h3>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* 이미지 영역 */}
        <div className="relative p-3">
          <div className="relative">
            <Image
              src={verificationImages[currentImageIndex].src}
              alt={verificationImages[currentImageIndex].alt}
              width={400}
              height={300}
              className="w-full h-auto rounded-lg"
            />
            
            {/* 이전/다음 버튼 */}
            {verificationImages.length > 1 && (
              <>
                {currentImageIndex > 0 && (
                  <button
                    onClick={handlePrevImage}
                    className="absolute left-1 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-40 text-white p-1 rounded-full hover:bg-opacity-60 transition-colors"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                )}
                {currentImageIndex < verificationImages.length - 1 && (
                  <button
                    onClick={handleNextImage}
                    className="absolute right-1 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-40 text-white p-1 rounded-full hover:bg-opacity-60 transition-colors"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                )}
              </>
            )}
          </div>

          {/* 이미지 설명 */}
          <div className="mt-3 text-center">
            <p className="text-sm md:text-lg text-gray-600">
              {verificationImages[currentImageIndex].description}
            </p>
          </div>

          {/* 이미지 인디케이터 */}
          {verificationImages.length > 1 && (
            <div className="flex justify-center mt-3 space-x-1">
              {verificationImages.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentImageIndex(index)}
                  className={`w-1.5 h-1.5 rounded-full transition-colors ${
                    index === currentImageIndex ? "bg-red-500" : "bg-gray-300"
                  }`}
                />
              ))}
            </div>
          )}
        </div>

        {/* 모달 푸터 */}
        <div className="flex justify-end gap-2 p-3 border-t">
          <button
            onClick={onClose}
            className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800 transition-colors"
          >
            취소
          </button>
          <button
            onClick={handleStartVerification}
            className="px-4 py-1.5 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700 transition-colors flex items-center gap-1.5"
          >
            <SiYoutube className="w-3 h-3" />
            구독 인증 시작하기
          </button>
        </div>
      </div>
    </div>
  );
}
